import { useContainer } from '@/plugins/inversify';
import { IConfigService, IConfigServiceId } from '@/services/IConfigService';
import { AppConfigDto } from '@/types/webapi';
import { defineStore } from 'pinia';
import { computed, ref, toValue } from 'vue';
import { DevModeConfiguration } from '../models';
import { startOfMinute } from 'date-fns';
import { useAppStore } from '@geta/kolumbus-frontend/features/common';
import { merge } from 'lodash-es';

export enum SearchMode {
    None = 0,
    Trip = 1,
    Departures = 2
}

export const useMainStore = defineStore('mainStore', () => {
    const container = useContainer();
    const configService = container.get<IConfigService>(IConfigServiceId);
    const appStore = useAppStore();
    const ready = ref(false);
    const isLoading = ref(false);
    const isDebugMode = computed(() => !!config.value.isDebugModeEnabled);
    const searchMode = ref<SearchMode>(SearchMode.Trip);
    const defaultConfig = ref<AppConfigDto>();
    const _config = ref<AppConfigDto & { supportedLanguages: string[] }>({
        kolumbusWebsiteBaseUrl: '',
        supportedLanguages: ['no', 'en'],
        googleMapsConfig: {
            apiKey: '',
            center: {
                latitude: 58.97,
                longitude: 5.7331
            }
        },
        silentLoginEnabled: true,
        defaultCulture: 'no',
        isDebugModeEnabled: false,
        devModeEnabled: false,
        searchConfig: {
            directSearchEnabled: false
        }
    });
    const config = computed({
        get() {
            return merge(_config.value, { supportedLanguages: appStore.config.supportedLanguages });
        },
        set(value) {
            _config.value = value;
        }
    });

    const showAppDrawer = ref(true);
    const isDevMode = ref(false);
    const devModeConfiguration = ref<DevModeConfiguration | undefined>();
    const currentTime = ref<Date>(startOfMinute(new Date()));

    async function loadConfig() {
        try {
            // fetch and set app config
            defaultConfig.value = await configService.getConfig();
            config.value = merge(config.value, defaultConfig.value);

            return toValue(config);
        } catch (e) {
            registerError(e);

            return null;
        }
    }

    function toggleSearch() {
        searchMode.value = searchMode.value === SearchMode.None ? SearchMode.Trip : SearchMode.None;
    }

    function registerError(error: any) {
        appStore.registerError(error);
    }

    function updateCurrentTime() {
        currentTime.value = startOfMinute(new Date());
    }

    const toExactMinute = 60000 - (new Date().getTime() % 60000); // calculate how many milliseconds to next exact minute
    setTimeout(() => {
        // set interval to update currentTime to exact minute
        setInterval(updateCurrentTime, 60000);
        // update currentTime to exact minute
        updateCurrentTime();
    }, toExactMinute);

    return {
        ready,
        isLoading,
        isDebugMode,
        searchMode,
        defaultConfig,
        config,
        showAppDrawer,
        isDevMode,
        devModeConfiguration,
        currentTime,
        loadConfig,
        toggleSearch,
        registerError
    };
});
